// ─────────────────────────────────────────────────────────────────────────────
// api.js — Centralized API client for NPQ VPN Enterprise
// Base URL is read from REACT_APP_API_BASE_URL (set per environment in .env.*)
//   dev:  http://localhost:3001/api/enterprise/v1   (.env.development)
//   prod: https://api.npqvpn.com/api/enterprise/v1  (.env.production)
//
// Token refresh strategy:
//   On 401 → attempt POST /auth/refresh (the refresh token rides an httpOnly
//            cookie sent automatically with credentials:"include" — it is NOT
//            in localStorage, so XSS can't read it)
//   Success → store new access token, retry original request once
//   Failure → clear storage, dispatch "auth:expired" → AuthContext redirects to /login
// ─────────────────────────────────────────────────────────────────────────────

import { getOrCreateDeviceId } from "./deviceService";

// Exported so non-fetch clients (e.g. the notification SSE EventSource, which
// can't go through this wrapper) can build absolute URLs against the same base.
export const BASE_URL = process.env.REACT_APP_API_BASE_URL;
if (!BASE_URL) {
  throw new Error(
    "REACT_APP_API_BASE_URL is not set. Check your .env.development or .env.production file."
  );
}

// ── Token helpers ─────────────────────────────────────────────────────────────
// Only the short-lived access token + user profile live in localStorage. The
// 7-day refresh token is an httpOnly cookie (set by the backend) and is never
// accessible to JS — see the refresh strategy above.
export const TokenStorage = {
  get:             ()        => localStorage.getItem("npq_token"),
  set:             (token)   => localStorage.setItem("npq_token", token),

  getUser: () => {
    try { return JSON.parse(localStorage.getItem("npq_user")); } catch { return null; }
  },
  setUser: (user) => localStorage.setItem("npq_user", JSON.stringify(user)),

  clear: () => {
    localStorage.removeItem("npq_token");
    localStorage.removeItem("npq_user");
    // Legacy: pre-cookie builds stored the refresh token here. Clean it up so a
    // stale token doesn't linger after upgrade.
    localStorage.removeItem("npq_refresh_token");
  },
};

// Endpoints that run BEFORE the user is authenticated. A 401 from these is a
// normal domain error (bad credentials / invalid or expired MFA code), NOT a
// session expiry — surface the message instead of clearing state + redirecting.
const PRE_AUTH_ENDPOINTS = new Set([
  "/auth/login",
  "/auth/mfa/verify",
  "/auth/mfa/resend",
  "/auth/forgot-password",
  "/auth/reset-password",
]);

// ── Session expiry ────────────────────────────────────────────────────────────
function expireSession() {
  TokenStorage.clear();
  window.dispatchEvent(new CustomEvent("auth:expired"));
}

// ── Token refresh ─────────────────────────────────────────────────────────────
// Single in-flight promise so concurrent 401s don't fire multiple refresh calls.
let refreshPromise = null;

async function attemptRefresh() {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    // No body — the refresh token travels as an httpOnly cookie. credentials:
    // "include" is required for the browser to send (and receive the rotated) cookie.
    const res = await fetch(`${BASE_URL}/auth/refresh`, {
      method:      "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "X-Device-ID":  getOrCreateDeviceId(),
      },
    });

    if (!res.ok) throw new Error(`Refresh failed: ${res.status}`);

    const data = await res.json();
    const newAccessToken = data?.access_token ?? data?.accessToken;
    if (!newAccessToken) throw new Error("Refresh response missing access_token");

    TokenStorage.set(newAccessToken);
    return newAccessToken;
  })();

  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

// ── Core fetch wrapper ────────────────────────────────────────────────────────
async function request(endpoint, options = {}, isRetry = false) {
  const token    = TokenStorage.get();
  const deviceId = getOrCreateDeviceId();

  const headers = {
    "Content-Type": "application/json",
    "Accept":       "application/json",
    "X-Device-ID":  deviceId,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  const config = {
    method:      options.method || "GET",
    // Send the httpOnly refresh cookie on auth calls (login/verify set it,
    // refresh/logout consume it). Harmless elsewhere — the cookie is path-scoped
    // to /auth, so it's only attached to those routes anyway.
    credentials: "include",
    headers,
    ...(options.body ? { body: JSON.stringify(options.body) } : {}),
  };

  try {
    const response = await fetch(`${BASE_URL}${endpoint}`, config);

    // ── 401 handling ───────────────────────────────────────────────────────
    // Skip refresh/expiry for pre-auth endpoints — the user isn't logged in yet,
    // so a 401 there is "bad credentials / invalid code", not a dead session.
    // Let it fall through to normal error parsing so the UI shows the message.
    if (response.status === 401 && !PRE_AUTH_ENDPOINTS.has(endpoint)) {
      // Don't try to refresh if:
      //   a) this is already a retry (avoid infinite loop)
      //   b) the failing request IS the refresh endpoint itself
      // (We can no longer check for a stored refresh token — it's an httpOnly
      // cookie. Instead we always attempt one refresh; if the cookie is missing
      // or invalid, the backend 401s and we expire below.)
      const isRefreshEndpoint = endpoint === "/auth/refresh";
      if (isRetry || isRefreshEndpoint) {
        expireSession();
        return null;
      }

      try {
        await attemptRefresh();
        // Retry the original request once with the new access token
        return request(endpoint, options, true);
      } catch {
        // Refresh itself failed — session is dead
        expireSession();
        return null;
      }
    }

    // ── Parse response ─────────────────────────────────────────────────────
    let data;
    try {
      data = await response.json();
    } catch {
      if (!response.ok) {
        const err = new Error(`Server error ${response.status}`);
        err.status = response.status;
        throw err;
      }
      return null;
    }

    if (!response.ok) {
      const msg = data?.message || data?.error || `Error ${response.status}`;
      const err = new Error(msg);
      err.status = response.status;
      if (data?.code) err.code = data.code;
      // Machine-readable failure class, where the endpoint offers one (the Node
      // Core download routes answer with `reason`: node_online,
      // binary_unavailable, unsupported_platform). Status alone can't separate
      // "this node is running" from any other 409, and the two need different
      // wording on screen.
      if (data?.reason) err.reason = data.reason;
      // Field-level validation feedback. The enterprise-nodes writes answer a 400
      // with { error, details: [...] }, where `error` is only the headline
      // ("Invalid node update") and `details` names the field that is wrong. Without
      // this the caller cannot tell the admin WHAT to fix.
      if (Array.isArray(data?.details)) err.details = data.details;
      throw err;
    }

    return data;
  } catch (err) {
    if (err instanceof TypeError) {
      throw new Error("Network error. Please check your connection.");
    }
    throw err;
  }
}

const api = {
  get:    (url, opts) => request(url, { ...opts, method: "GET"    }),
  post:   (url, body) => request(url, { method: "POST",   body    }),
  put:    (url, body) => request(url, { method: "PUT",    body    }),
  patch:  (url, body) => request(url, { method: "PATCH",  body    }),
  delete: (url, body) => request(url, { method: "DELETE", body    }),
};

export default api;
